import React, { useState, useEffect } from "react";
import { compose } from "redux";
import { connect } from "react-redux";
import { DotsItem } from "@nivo/core";
import { ResponsiveLine } from "@nivo/line";
import numeral from "numeral";
import { addWeeks, format } from "date-fns";

import { SAMPLEDATA } from "./sampleData";
import "./styles.css";

const DashedLine = ({ series, lineGenerator, xScale, yScale }) => (
  series.map(({ id, data, color }, index) => (
    <path
      key={id}
      d={lineGenerator(
        data.map(d => ({
          x: xScale(d.data.x),
          y: yScale(d.data.y)
        }))
      )}
      fill="none"
      stroke={color}
      style={index === 0 || index === 1 ? { strokeWidth: 3 } : { strokeWidth: 0 }}
    />
  ))
);

const LastPoint = ({ points, ...props }) => (
  <g>
    {points.map(point => (point.id === "Current Rate.0" ? (
      <DotsItem
        key={point.id}
        x={point.x}
        y={point.y}
        datum={point.data}
        size={10}
        color={point.color}
      />
    ) : (
      <DotsItem
        key={point.id}
        x={point.x}
        y={point.y}
        datum={point.data}
        size={props.pointSize}
        color={point.color}
      />
    )))}
  </g>
);

const BreakevenLineChart = ({ snapshot, breakEvenResults, companyPrimaryColor, companySecondaryColor, providedCurrentRate, providedBreakevenRate }) => {
  const snapshotData = snapshot?.treasurySnapshot?.data || [];
  const NUMBER_OF_WEEKS = snapshotData.length || 1;
  // const NUMBER_OF_WEEKS = SAMPLEDATA.length; // This is for SAMPLEDATA

  const {
    rate: latestRate = 0.01499,
    createdAt: latestDate = "2021-08-12T05:06:43.000Z"
  } = snapshot?.latestRate?.data || {};
  const { breakevenTreasury: breakevenTreasuryRate = 0.016 } = breakEvenResults;

  const [currentRate, setCurrentRate] = useState(0);
  const [breakevenRate, setBreakevenRate] = useState(0);

  useEffect(() => {
    if (!providedCurrentRate || !providedBreakevenRate) {
      setCurrentRate(latestRate);
      setBreakevenRate(breakevenTreasuryRate);
    }

    if (providedCurrentRate || providedBreakevenRate) {
      setCurrentRate(providedCurrentRate);
      setBreakevenRate(providedBreakevenRate);
    }
  }, [providedCurrentRate, providedBreakevenRate, breakEvenResults]);

  const firstRate = snapshotData?.[0]?.rate;
  const lowestRate = Math.min(...snapshotData?.map(x => x.rate));
  const adjustedLowestRate = firstRate === lowestRate ? firstRate : lowestRate * 0.8;
  const adjustedLatestRate = currentRate - adjustedLowestRate;
  const snapshotDataArray = snapshotData.map(snap => ({
    x: snap.createdAt,
    y: numeral(snap.rate - adjustedLowestRate).format("0.00%")
  }));

  const nullSnapshotDataArray = snapshotData.map(snap => ({
    x: snap.createdAt,
    y: null
  }));

  const todaysDateAndRate = [{
    x: latestDate,
    y: numeral(adjustedLatestRate).format("0.00%")
  }];

  const nullTodaysDateAndRate = [{
    x: latestDate,
    y: null
  }];

  const projectedBreakevenData = isNulled => Array(NUMBER_OF_WEEKS).fill().map((_, idx) => {
    const newDate = addWeeks(new Date(latestDate), idx);
    const newDateFormatted = format(newDate, "yyyy-MM-dd/'T/'HH:mm:ss/'z/'");
    const rate = (((breakevenRate - adjustedLatestRate) / NUMBER_OF_WEEKS) * (idx + 1)) + adjustedLatestRate;

    return isNulled ?
      ({
        x: newDateFormatted,
        y: null
      }) :
      ({
        x: newDateFormatted,
        y: numeral(rate).format("0.00%")
      });
  });

  const emptyProjectedData = () => Array(NUMBER_OF_WEEKS).fill().map((_, idx) => {
    const newDate = addWeeks(new Date(latestDate), NUMBER_OF_WEEKS + idx);
    const newDateFormatted = format(newDate, "yyyy-MM-dd/'T/'HH:mm:ss/'z/'");

    return ({
      x: newDateFormatted,
      y: null
    });
  });

  const formattedData = [
    {
      id: "Historical Treasury Y10 Rate",
      data: [...snapshotDataArray, ...todaysDateAndRate, ...projectedBreakevenData(true), ...emptyProjectedData()]
    },
    {
      id: "Current Rate",
      data: [...nullSnapshotDataArray, ...todaysDateAndRate, ...projectedBreakevenData(true), ...emptyProjectedData()]
    },
    {
      id: "Breakeven Rate",
      data: [...nullSnapshotDataArray, ...nullTodaysDateAndRate, ...projectedBreakevenData(false), ...emptyProjectedData()]
    }
  ];

  /*
   * #region Sample Data
   * const sampleFirstRate = SAMPLEDATA[0].rate;
   * const sampleLowestRate = Math.min(...SAMPLEDATA.map(x => x.rate)) * 0.8;
   * const sampleAdjustedLowestRate = sampleFirstRate === sampleLowestRate ? sampleFirstRate : sampleLowestRate * 0.8;
   */

  /*
   * const sampleSnapshotData = SAMPLEDATA.map(snap => ({
   *     x: snap.createdAt,
   *     y: numeral(snap.rate - sampleAdjustedLowestRate).format('0.00%')
   * }));
   */

  /*
   * const nullSampleSnapshotData = SAMPLEDATA.map(snap => ({
   *     x: snap.createdAt,
   *     y: null
   * }));
   */

  // const sampleFormattedData = [
  //     {
  //         id: 'Historical Treasury Y10 Rate',
  //         data: [...sampleSnapshotData, ...todaysDateAndRate, ...projectedBreakevenData(true), ...emptyProjectedData()]
  //     },
  //     {
  //         id: 'Current Rate',
  //         data: [...nullSampleSnapshotData, ...todaysDateAndRate, ...projectedBreakevenData(true), ...emptyProjectedData()]
  //     },
  //     {
  //         id: 'Breakeven Rate',
  //         data: [...nullSampleSnapshotData, ...nullTodaysDateAndRate, ...projectedBreakevenData(false), ...emptyProjectedData()]
  //     }
  // ];
  // //#endregion

  return (
    <>
      <div className="e-breakeven-line-chart-treasury-text-block-wrapper e-current">
        <div className="e-treasury-text-wrapper">
          <span>The 10T today is</span>
          <span className="e-treasury-text" style={{ color: companyPrimaryColor }}>{numeral(currentRate).format("0.00%")}</span>
        </div>
      </div>
      <div className="e-breakeven-line-chart-treasury-text-block-wrapper e-breakeven">
        <div className="e-treasury-text-wrapper">
          <span>If the 10T exceeds</span>
          <span className="e-treasury-text" style={{ color: companySecondaryColor }}>{numeral(breakevenRate).format("0.00%")}</span>
        </div>
      </div>
      <div className="e-breakeven-line-chart-treasury-text-block-wrapper e-wishes">
        <div className="e-treasury-text-wrapper">
          <span>you'll wish you had</span>
          <span>refinanced today</span>
        </div>
      </div>
      <ResponsiveLine
        data={formattedData}
        height={200}
        width={310}
        colors={[companyPrimaryColor, companyPrimaryColor, companySecondaryColor]}
        margin={{ top: 40, left: 20, bottom: 30 }}
        pointSize={2}
        axisBottom={{
          tickSize: 0,
          format: () => ""
        }}
        axisLeft={{
          tickSize: 0,
          format: () => ""
        }}
        theme={{
          axis: {
            domain: {
              line: {
                strokeWidth: 2,
                stroke: companyPrimaryColor
              }
            }
          }
        }}
        layers={[DashedLine, "points", "axes", "markers", LastPoint]}
      />
    </>
  );
};

const mapStateToProps = ({ treasurySnapshot, testHarness }) => ({
  snapshot: treasurySnapshot,
  breakEvenResults: testHarness?.refiBreakEvenResults?.data || []
});

export default compose(connect(mapStateToProps))(BreakevenLineChart);
